import React from "react";
import { Link } from "react-router-dom";
import { useCompare } from "../context/CompareContext";
import { formatTT } from "../lib/api";
import { Scale, Star, ArrowUpRight, Sparkles } from "lucide-react";
import ApplyDialog from "./ApplyDialog";

const ProductCard = ({ product }) => {
    const { has, add, remove, items } = useCompare();
    const inCompare = has(product.id);
    const disabledCompare = !inCompare && items.length >= 3;
    const wrongType = !inCompare && items.length > 0 && items[0].type !== product.type;

    const onToggleCompare = (e) => {
        e.preventDefault();
        if (inCompare) remove(product.id);
        else add(product);
    };

    return (
        <div
            className={`grid-card bg-white p-6 flex flex-col relative ${product.featured ? "ring-1 ring-[#D1A77E]/40" : ""}`}
            data-testid={`product-card-${product.id}`}
        >
            {product.featured && (
                <div
                    className="absolute -top-px -left-px flex items-center gap-1 px-2 py-1 bg-[#0B192C] text-[#D1A77E] label-overline text-[10px]"
                    data-testid={`sponsored-badge-${product.id}`}
                    title={`Sponsored placement — ${product.sponsor_tier || "featured"} tier`}
                >
                    <Sparkles className="w-3 h-3" strokeWidth={1.5}/>
                    Sponsored
                </div>
            )}
            <div className={`flex items-start justify-between mb-4 ${product.featured ? "mt-3" : ""}`}>
                <div>
                    <div className="label-overline text-[#718096] mb-1">{product.provider}</div>
                    <Link to={`/products/${product.id}`} className="block">
                        <h3 className="font-serif-display text-2xl leading-tight hover:underline decoration-[#D1A77E] underline-offset-4">
                            {product.name}
                        </h3>
                    </Link>
                </div>
                <div className="flex items-center gap-1 text-sm" data-testid={`rating-${product.id}`}>
                    <Star className="w-3.5 h-3.5 fill-[#D1A77E] stroke-[#D1A77E]" />
                    <span className="font-mono-body">{product.rating?.toFixed(1) || "—"}</span>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-5 text-sm">
                <div>
                    <div className="label-overline text-[#718096]">{product.type === "credit_card" ? "APR" : "Rate"}</div>
                    <div className="font-mono-body text-lg">{product.interest_rate}%</div>
                </div>
                {product.type === "loan" ? (
                    <>
                        <div>
                            <div className="label-overline text-[#718096]">Amount</div>
                            <div className="font-mono-body text-sm">{formatTT(product.min_amount)}–{formatTT(product.max_amount)}</div>
                        </div>
                        <div>
                            <div className="label-overline text-[#718096]">Tenure</div>
                            <div className="font-mono-body">{product.min_tenure_months}–{product.max_tenure_months} mo</div>
                        </div>
                        <div>
                            <div className="label-overline text-[#718096]">Min. Income</div>
                            <div className="font-mono-body">{formatTT(product.min_income)}/mo</div>
                        </div>
                    </>
                ) : (
                    <>
                        <div>
                            <div className="label-overline text-[#718096]">Annual Fee</div>
                            <div className="font-mono-body">{formatTT(product.annual_fee)}</div>
                        </div>
                        <div>
                            <div className="label-overline text-[#718096]">Min. Income</div>
                            <div className="font-mono-body">{formatTT(product.min_income)}/mo</div>
                        </div>
                        <div>
                            <div className="label-overline text-[#718096]">Min. Credit</div>
                            <div className="font-mono-body">{product.min_credit_score}</div>
                        </div>
                    </>
                )}
            </div>

            <div className="flex flex-wrap gap-1.5 mb-5">
                {product.features?.slice(0, 3).map((f) => (
                    <span key={f} className="text-xs px-2 py-1 bg-[#EAE7E1] text-[#0B192C]">
                        {f}
                    </span>
                ))}
            </div>

            <div className="mt-auto flex gap-2 pt-4 border-t border-[#0B192C]/10">
                <ApplyDialog product={product} trigger={
                    <button
                        data-testid={`apply-now-${product.id}`}
                        className="btn-accent flex-1 px-4 py-2.5 label-overline flex items-center justify-center gap-1.5"
                    >
                        Apply Now <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                } />
                <button
                    onClick={onToggleCompare}
                    disabled={disabledCompare || wrongType}
                    data-testid={`compare-toggle-${product.id}`}
                    title={wrongType ? "Mixing types not allowed" : disabledCompare ? "Max 3 items" : ""}
                    className={`px-3 py-2.5 border label-overline flex items-center gap-1.5 transition-colors ${
                        inCompare ? "border-[#1E3A2B] bg-[#1E3A2B] text-[#F8F7F4]" : "border-[#0B192C]/30 text-[#0B192C] hover:border-[#0B192C]"
                    } ${(disabledCompare || wrongType) ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                    <Scale className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {inCompare ? "Added" : "Compare"}
                </button>
            </div>
        </div>
    );
};

export default ProductCard;
